import {
  useGetFindingsStatusCountQuery,
  useLazyGetFindingsQuery,
  useUpdateBatchFindingsMutation,
  useProjectId,
  usePackageId,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { FindingStatus } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { Loader2Icon, Trash2Icon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { ConfirmDialog, errorToast, successToast } from '@/components';

import { FindingsFilterState, hasActiveFindingsFilter } from './types';

interface DeleteNonRelevantFindingsButtonProps {
  filters: FindingsFilterState;
}

/**
 * Button to soft-delete every finding currently marked as Not Relevant.
 * Honors the active findings filter state (processor, documents, labels,
 * search) so that only Not Relevant findings matching the filters are
 * affected. Shows a confirmation dialog before performing the batch delete.
 */
export const DeleteNonRelevantFindingsButton = ({
  filters,
}: DeleteNonRelevantFindingsButtonProps) => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [open, setOpen] = useState(false);

  const [updateFindings, { isLoading: isUpdating }] = useUpdateBatchFindingsMutation();
  const [fetchFindings, { isFetching: isFetchingFindings }] = useLazyGetFindingsQuery();

  const { processorName, blobIds, labels, search } = filters;

  // Only the count is needed to render the button; the actual Not Relevant
  // findings are fetched lazily on confirm, so opening the tab stays cheap.
  const { data: statusCount, isLoading: isLoadingCount } = useGetFindingsStatusCountQuery({
    projectId,
    packageId,
    processorName,
    blobIds: blobIds.length > 0 ? blobIds : undefined,
    labels: labels.length > 0 ? labels : undefined,
    search: search.trim() || undefined,
  });

  const count = statusCount?.[FindingStatus.NOT_RELEVANT] ?? 0;

  const isLoading = isLoadingCount || isFetchingFindings || isUpdating;

  const processorLabel = processorName ? formatEnumToReadableText(processorName) : null;
  const otherFiltersActive = hasActiveFindingsFilter({ ...filters, processorName: undefined });

  const buttonSuffix = processorLabel
    ? otherFiltersActive
      ? ` for filtered ${processorLabel}`
      : ` for ${processorLabel}`
    : otherFiltersActive
      ? ' for current filters'
      : '';

  const dialogSubject = processorLabel
    ? `${processorLabel} `
    : otherFiltersActive
      ? 'filtered '
      : '';

  const handleDelete = useCallback(async () => {
    try {
      const { results: nonRelevantFindings } = await fetchFindings({
        projectId,
        packageId,
        page: 'all',
        status: FindingStatus.NOT_RELEVANT,
        processorName,
        blobIds: blobIds.length > 0 ? blobIds : undefined,
        labels: labels.length > 0 ? labels : undefined,
        search: search.trim() || undefined,
      }).unwrap();
      await updateFindings({
        projectId,
        packageId,
        data: nonRelevantFindings.map((finding) => ({
          id: finding.id,
          status: FindingStatus.DELETED,
        })),
      }).unwrap();
      successToast({
        message: `Successfully deleted ${nonRelevantFindings.length} not relevant finding${
          nonRelevantFindings.length !== 1 ? 's' : ''
        }`,
      });
    } catch (error) {
      console.error('Failed to delete not relevant findings:', error);
      errorToast({ message: 'Failed to delete not relevant findings. Please try again.' });
    } finally {
      setOpen(false);
    }
  }, [projectId, packageId, processorName, blobIds, labels, search, fetchFindings, updateFindings]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant="destructive"
          disabled={isLoading || count === 0}
          className="gap-2 max-w-xs"
          title={`Delete Not Relevant${buttonSuffix} (${count})`}
        >
          {isLoading ? (
            <Loader2Icon className="h-4 w-4 animate-spin shrink-0" />
          ) : (
            <Trash2Icon className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate min-w-0">Delete Not Relevant{buttonSuffix}</span>
          <span className="shrink-0">({count})</span>
        </Button>
      }
      title="Delete all not relevant findings?"
      description={
        <>
          This will mark {count} {dialogSubject}not relevant finding
          {count !== 1 ? 's' : ''} as deleted. This action cannot be easily undone.
        </>
      }
      confirmLabel="Delete Not Relevant"
      isLoading={isUpdating}
      onConfirm={handleDelete}
    />
  );
};
